import React, { useEffect, useRef } from 'react';

const SunsetBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Sunset color palette
    const skyColors = [
      { color: 'rgba(255, 94, 77, 0.95)', y: 0 },      // Deep coral
      { color: 'rgba(255, 145, 77, 0.95)', y: 0.2 },   // Orange
      { color: 'rgba(255, 183, 77, 0.95)', y: 0.4 },   // Light orange
      { color: 'rgba(255, 214, 102, 0.95)', y: 0.6 },  // Golden
      { color: 'rgba(190, 90, 140, 0.95)', y: 0.85 },  // Dusky pink
      { color: 'rgba(50, 20, 80, 1)', y: 1 },          // Deep purple horizon
    ];

    // Cloud parameters
    const clouds = Array.from({ length: 6 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height * 0.6,
      width: Math.random() * 200 + 150,
      height: Math.random() * 40 + 30,
      speed: Math.random() * 0.2 + 0.1,
      opacity: Math.random() * 0.3 + 0.2,
    }));

    // Birds (flying silhouettes)
    const birds = Array.from({ length: 8 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height * 0.5,
      speed: Math.random() * 0.5 + 0.3,
      wingPhase: Math.random() * Math.PI * 2,
      size: Math.random() * 8 + 6,
    }));

    let time = 0;

    // Animation loop
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.01;

      // Draw gradient sky
      drawSky();

      // Draw sun
      drawSun();

      // Draw clouds
      clouds.forEach((cloud) => {
        drawCloud(cloud);
      });

      // Draw birds
      birds.forEach((bird) => {
        drawBird(bird);
      });

      animationFrameId.current = requestAnimationFrame(animate);
    }

    // Draw gradient sky
    function drawSky() {
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);

      skyColors.forEach(({ color, y }) => {
        gradient.addColorStop(y, color);
      });

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // Draw sun
    function drawSun() {
      const sunY = canvas.height * 0.65 + Math.sin(time * 0.3) * 5;
      const sunX = canvas.width / 2 + Math.cos(time * 0.2) * 20;

      // Sun glow
      const gradient = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 120);
      gradient.addColorStop(0, 'rgba(255, 200, 100, 0.4)');
      gradient.addColorStop(0.5, 'rgba(255, 150, 80, 0.2)');
      gradient.addColorStop(1, 'rgba(255, 100, 70, 0)');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(sunX, sunY, 120, 0, Math.PI * 2);
      ctx.fill();

      // Sun core
      ctx.fillStyle = 'rgba(255, 220, 120, 0.6)';
      ctx.beginPath();
      ctx.arc(sunX, sunY, 50, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw cloud
    function drawCloud(cloud: {
      x: number;
      y: number;
      width: number;
      height: number;
      speed: number;
      opacity: number;
    }) {
      // Update position
      cloud.x -= cloud.speed;

      // Reset if out of bounds
      if (cloud.x + cloud.width < 0) {
        cloud.x = canvas.width;
        cloud.y = Math.random() * canvas.height * 0.6;
      }

      // Draw cloud
      ctx.fillStyle = `rgba(255, 180, 150, ${cloud.opacity})`;

      // Cloud is made of overlapping circles
      const circles = 5;
      for (let i = 0; i < circles; i++) {
        const circleX = cloud.x + (cloud.width / circles) * i;
        const circleY = cloud.y + Math.sin((i / circles) * Math.PI) * cloud.height * 0.3;
        const radius = cloud.height * (0.8 - Math.abs(i - circles / 2) * 0.15);

        ctx.beginPath();
        ctx.arc(circleX, circleY, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw bird
    function drawBird(bird: {
      x: number;
      y: number;
      speed: number;
      wingPhase: number;
      size: number;
    }) {
      // Update position
      bird.x += bird.speed;
      bird.y += Math.sin(time + bird.wingPhase) * 0.2;
      bird.wingPhase += 0.1;

      // Reset if out of bounds
      if (bird.x > canvas.width) {
        bird.x = -20;
        bird.y = Math.random() * canvas.height * 0.5;
      }

      // Draw bird as V shape (flying silhouette)
      ctx.strokeStyle = 'rgba(100, 50, 30, 0.5)';
      ctx.lineWidth = 2;

      const wingFlap = Math.sin(bird.wingPhase) * 0.3;

      ctx.beginPath();
      // Left wing
      ctx.moveTo(bird.x, bird.y);
      ctx.lineTo(bird.x - bird.size + wingFlap * bird.size, bird.y - bird.size * 0.8);

      // Right wing
      ctx.moveTo(bird.x, bird.y);
      ctx.lineTo(bird.x + bird.size - wingFlap * bird.size, bird.y - bird.size * 0.8);

      ctx.stroke();
    }

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
    />
  );
};

export default SunsetBackground;
